// hh-9r1 — ScheduleRuleForm.
//
// Inline form for manual maintenance-regulation editing on the EquipmentCard:
// add a new ScheduleRule or edit an existing one's title / period. Backed by
// ScheduleController (POST add, PATCH update) — both go through MutationService
// and are undo-able. Safety flag is set only on add: the backend PATCH does
// not change isSafetyCritical, and a safety rule must not be silently
// downgraded from this form.

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { T, Btn } from '../../tokens';
import { schedule as scheduleApi, humanizeError } from '../../api';
import type { ScheduleRule } from '../../api/endpoints/schedule.js';

export interface ScheduleRuleFormProps {
  readonly equipmentId: string;
  /** Когда задано — режим редактирования; иначе — добавление нового пункта. */
  readonly rule?: ScheduleRule;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

/**
 * Как задаётся следующее ТО (aidepo-12): `period` — выбирается периодичность,
 * дату считает бэкенд; `date` — задаётся конкретная дата (явный nextAt).
 */
type ScheduleMode = 'period' | 'date';

const PERIOD_PRESETS: readonly { readonly label: string; readonly months: number }[] = [
  { label: '3 мес', months: 3 },
  { label: '6 мес', months: 6 },
  { label: '1 год', months: 12 },
  { label: '2 года', months: 24 },
  { label: '5 лет', months: 60 },
];

export function ScheduleRuleForm({
  equipmentId,
  rule,
  onSaved,
  onCancel,
}: ScheduleRuleFormProps): JSX.Element {
  const isEdit = rule !== undefined;
  const [title, setTitle] = useState<string>(rule?.title ?? '');
  const [months, setMonths] = useState<number>(rule?.periodMonths ?? 12);
  const [safety, setSafety] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Режим задания следующего ТО (aidepo-12) + значение date-пикера.
  const [mode, setMode] = useState<ScheduleMode>('period');
  const [dateValue, setDateValue] = useState<string>(rule?.nextAt?.slice(0, 10) ?? '');

  const canSubmit =
    title.trim().length > 0 && (mode === 'period' ? months > 0 : dateValue !== '');

  const submit = (): void => {
    const trimmed = title.trim();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    // В режиме даты отправляем явный nextAt; периодичность всё равно нужна
    // (бэкенд требует periodMonths > 0 и использует её для повторения ТО).
    const nextAt =
      mode === 'date' ? new Date(`${dateValue}T12:00:00`).toISOString() : undefined;
    const op = isEdit
      ? scheduleApi.updateScheduleRule(equipmentId, rule.id, {
          title: trimmed,
          ...(mode === 'period'
            ? { periodMonths: months }
            : { nextAt: nextAt as string }),
        })
      : scheduleApi.addScheduleRule(equipmentId, {
          title: trimmed,
          periodMonths: months,
          ...(nextAt !== undefined ? { nextAt } : {}),
          isSafetyCritical: safety,
          fromCatalog: false,
        });
    op.then(() => onSaved()).catch((err: unknown) => {
      setError(humanizeError(err, 'save'));
      setBusy(false);
    });
  };

  return (
    <div
      style={{
        marginTop: 10,
        background: T.surface,
        border: `1px solid ${T.brandSoft2}`,
        borderRadius: T.r3,
        padding: 14,
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 10 }}>
        {isEdit ? 'Изменить пункт регламента' : 'Новый пункт регламента'}
      </div>

      <div style={labelStyle}>Что делать</div>
      <input
        type="text"
        value={title}
        disabled={busy}
        placeholder="Например: чистка фильтра"
        onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
        style={inputStyle}
      />

      <div style={{ ...labelStyle, marginTop: 12 }}>Когда следующее ТО</div>
      <div style={{ display: 'flex', gap: 6 }}>
        {(['period', 'date'] as const).map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              disabled={busy}
              onClick={() => setMode(m)}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: T.font,
                borderRadius: 999,
                cursor: busy ? 'default' : 'pointer',
                background: active ? T.brandSoft : T.surface,
                border: `1px solid ${active ? T.brandSoft2 : T.border}`,
                color: active ? T.brandDeep : T.ink2,
              }}
            >
              {m === 'period' ? 'По периодичности' : 'Конкретная дата'}
            </button>
          );
        })}
      </div>

      {mode === 'period' ? (
        <>
          <div style={{ ...labelStyle, marginTop: 12 }}>Как часто</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PERIOD_PRESETS.map((p) => {
              const active = p.months === months;
              return (
                <button
                  key={p.months}
                  type="button"
                  disabled={busy}
                  onClick={() => setMonths(p.months)}
                  style={{
                    padding: '7px 12px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    fontFamily: T.font,
                    borderRadius: 999,
                    cursor: busy ? 'default' : 'pointer',
                    background: active ? T.brandSoft : T.surface,
                    border: `1px solid ${active ? T.brandSoft2 : T.border}`,
                    color: active ? T.brandDeep : T.ink2,
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: T.ink3 }}>или вручную, мес.:</span>
            <input
              type="number"
              min={1}
              value={String(months)}
              disabled={busy}
              onInput={(e) => {
                const n = Number.parseInt((e.target as HTMLInputElement).value, 10);
                if (Number.isFinite(n) && n > 0) setMonths(n);
              }}
              style={{ ...inputStyle, width: 80 }}
            />
          </div>
        </>
      ) : (
        <>
          <div style={{ ...labelStyle, marginTop: 12 }}>Дата следующего ТО</div>
          <input
            type="date"
            value={dateValue}
            disabled={busy}
            onInput={(e) => setDateValue((e.target as HTMLInputElement).value)}
            style={inputStyle}
          />
        </>
      )}

      {!isEdit && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            fontSize: 13,
            color: T.ink2,
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={safety}
            disabled={busy}
            onChange={(e) => setSafety((e.target as HTMLInputElement).checked)}
          />
          Важный для безопасности пункт
        </label>
      )}

      {error !== null && (
        <div style={{ fontSize: 12.5, color: T.danger, fontWeight: 600, marginTop: 10 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Btn kind="ghost" size="sm" onClick={onCancel} disabled={busy} style={{ flex: 1 }}>
          Отмена
        </Btn>
        <Btn
          kind="primary"
          size="sm"
          icon="check"
          onClick={submit}
          disabled={busy || !canSubmit}
          style={{ flex: 1.4 }}
        >
          {busy ? 'Сохраняю…' : 'Сохранить'}
        </Btn>
      </div>
    </div>
  );
}

const labelStyle: JSX.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: T.ink3,
  marginBottom: 6,
};

const inputStyle: JSX.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: T.font,
  color: T.ink,
  background: T.bg,
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  outline: 'none',
};
